import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Card, CardContent } from '@/components/ui/card';
import { Building2, Users, Globe, Shield, Sparkles, MapPin } from 'lucide-react';

const STATS = [
  { icon: Building2, label: 'Verified agencies', value: '500+' },
  { icon: Users, label: 'Active users monthly', value: '120k+' },
  { icon: MapPin, label: 'Cities covered', value: '20+' },
  { icon: Globe, label: 'Languages', value: 'EN · AM · OM' },
];

const VALUES = [
  { icon: Shield, title: 'Trust first', body: 'Every agency, agent and developer goes through SuperAdmin verification before listings go live.' },
  { icon: Sparkles, title: 'Built for Ethiopia', body: 'Dual ETB/USD pricing, hectares & sqft, Amharic + Afaan Oromo — the platform speaks the local market.' },
  { icon: Users, title: 'For everyone', body: 'Buyers, renters, agents, agencies, developers and diaspora investors all have purpose-built tools.' },
];

export default function About() {
  return (
    <Layout>
      <SEOHead
        title="About Ethio Real Estates"
        description="Ethiopia's largest real estate marketplace — connecting buyers, renters, agents, agencies and developers across the country."
        canonical="/about"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: 'About Ethio Real Estates',
          url: 'https://www.ethiorealestates.com/about',
        }}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-primary/10 via-background to-background border-b">
        <div className="container py-16 md:py-24">
          <div className="max-w-3xl">
            <h1 className="font-display text-4xl md:text-5xl font-bold leading-tight">
              Ethiopia's most trusted real estate marketplace.
            </h1>
            <p className="mt-5 text-lg text-muted-foreground">
              Ethio Real Estates connects home buyers, renters, agents, agencies and developers
              across every major Ethiopian city — with verified listings, transparent pricing
              and tools built for the local market.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="container py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {STATS.map(({ icon: Icon, label, value }) => (
            <Card key={label}>
              <CardContent className="p-5">
                <Icon className="h-5 w-5 text-primary" />
                <div className="mt-3 text-2xl md:text-3xl font-bold">{value}</div>
                <div className="text-sm text-muted-foreground">{label}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Mission */}
      <section className="container py-12 grid md:grid-cols-2 gap-10 items-start">
        <div>
          <h2 className="font-display text-2xl md:text-3xl font-bold mb-4">Our mission</h2>
          <p className="text-muted-foreground leading-relaxed">
            Real estate in Ethiopia has long been opaque — fragmented listings, unverified agents
            and language barriers. We exist to bring everything into one place: a marketplace where
            every listing is moderated, every agency is vetted, and finding (or selling) a property
            takes minutes, not weeks.
          </p>
        </div>
        <div>
          <h2 className="font-display text-2xl md:text-3xl font-bold mb-4">What we do</h2>
          <ul className="space-y-2 text-muted-foreground leading-relaxed">
            <li>• Buy, rent and lease residential, commercial and land properties.</li>
            <li>• Discover new development projects with floor plans and payment schedules.</li>
            <li>• Find verified agencies and developers by city.</li>
            <li>• Calculate mortgages and construction costs in ETB or USD.</li>
            <li>• Read market news, blogs and community discussions.</li>
          </ul>
        </div>
      </section>

      {/* Values */}
      <section className="bg-muted/30 border-y">
        <div className="container py-16">
          <h2 className="font-display text-2xl md:text-3xl font-bold mb-8 text-center">What we stand for</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {VALUES.map(({ icon: Icon, title, body }) => (
              <Card key={title}>
                <CardContent className="p-6">
                  <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center mb-3">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container py-16 text-center">
        <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">Get in touch</h2>
        <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
          Questions about a listing, becoming a verified agency, or running ads on Ethio Real Estates?
          We'd love to hear from you.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link to="/contact" className="px-5 py-2.5 rounded-md bg-primary text-primary-foreground font-semibold hover:bg-primary/90">
            Contact us
          </Link>
          <Link to="/advertise" className="px-5 py-2.5 rounded-md border font-semibold hover:bg-muted">
            Advertise with us
          </Link>
        </div>
      </section>
    </Layout>
  );
}
